import moment from 'moment';
import countries from 'i18n-iso-countries';
import { List, Map } from 'immutable';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { ContactType, extractContactName } from 'flow/utils/extension-utils';
import { newestFirstSorter } from 'flow/utils/sorter-utils';
import parsePhoneNumberFromString from 'libphonenumber-js';

export const getName = (callRecord) => {
    if (!callRecord) {
        throw Error('Invalid callRecord parameter');
    }

    const firstName = callRecord.getIn(['contact', 'firstName']);
    const lastName = callRecord.getIn(['contact', 'lastName']);

    if (firstName && !lastName) {
        return firstName;
    }

    if (!firstName && lastName) {
        return lastName;
    }

    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    }

    return undefined;
};

export const getDepartment = (callRecord) => {
    if (!callRecord) {
        throw Error('Invalid callRecord parameter');
    }

    const department = callRecord.getIn(['contact', 'department']);
    const isBound =
        callRecord.getIn(['contact', 'type']) === ContactType.bound.key;
    if (department && isBound) {
        return department;
    }

    return undefined;
};

const getMeetingName = (callRecord, i18next) => {
    const conferenceDTO = callRecord.get('conferenceDTO');
    if (conferenceDTO) {
        return conferenceDTO.get('name')
            ? conferenceDTO.get('name')
            : i18next.t('videoconference.meeting');
    }
    return undefined;
};

const getChatSession = (callRecord) => {
    const conferenceDTO = callRecord.get('conferenceDTO');
    if (conferenceDTO) {
        return conferenceDTO.get('chatSessionId')
            ? conferenceDTO.get('chatSessionId')
            : undefined;
    }
    return undefined;
};

export const isCallRecordAMeeting = (callRecord) =>
    !!callRecord && callRecord.hasIn(['conferenceDTO', 'chatSessionId']);

export const isConferenceAVideoMeeting = (conferenceDTO) =>
    !!conferenceDTO && conferenceDTO.has('chatSessionId');

const getNumber = (callRecord) => {
    return callRecord.getIn(['number', 'number']);
};

export const getCountryCodeFromCallRecord = (callRecord) => {
    if (!isE164Number(callRecord)) {
        return undefined;
    }

    return callRecord.getIn(['number', 'countryCode']);
};

const isE164Number = (callRecord) => {
    const callRecordType = callRecord?.getIn(['number', 'type']);

    return callRecordType === 'e164';
};

export const getCallRecordDisplayText = (
    callRecord,
    loggedInUserCountryCode,
    i18next,
    title,
) => {
    if (!callRecord) {
        throw Error('Invalid callRecord parameter');
    }

    if (isCallRecordAMeeting(callRecord)) {
        return getMeetingName(callRecord, i18next);
    }

    const name = getName(callRecord);
    if (name && !title) {
        return name;
    }

    const number = getNumber(callRecord);
    const isTechnicalNumber =
        callRecord.getIn(['number', 'type'], '') === 'technical_number';
    let prettyformattedNumber = '';
    if (!isTechnicalNumber) {
        prettyformattedNumber =
            number && isE164Number(callRecord)
                ? prettyFormatE164Number(number, loggedInUserCountryCode)
                : number;
    }

    const department = getDepartment(callRecord);
    if (department && title !== department) {
        if (title && prettyformattedNumber && title !== prettyformattedNumber) {
            return `${prettyformattedNumber}\u00A0\u00A0\u00A0${department}`;
        }
        return department;
    }

    const company = callRecord.getIn(['contact', 'companyName']);
    if (company && title !== company) {
        if (title && prettyformattedNumber && title !== prettyformattedNumber) {
            return `${prettyformattedNumber}\u00A0\u00A0\u00A0${company}`;
        }
        return company;
    }

    if (prettyformattedNumber && prettyformattedNumber !== title) {
        return prettyformattedNumber;
    }

    const countryCode = getCountryCodeFromCallRecord(callRecord);
    if (countryCode && title && title !== countryCode) {
        return countries.getName(countryCode, moment.locale());
    }

    return null;
};

export const getCallRecordDisplayTextTW = (
    callRecord,
    loggedInUserCountryCode,
    i18next,
    numberWrapper,
    title,
) => {
    if (!callRecord) {
        throw Error('Invalid callRecord parameter');
    }

    if (isCallRecordAMeeting(callRecord)) {
        return getMeetingName(callRecord, i18next);
    }

    const name = getName(callRecord);
    if (name && !title) {
        return name;
    }

    const number = getNumber(callRecord);
    const isTechnicalNumber =
        callRecord.getIn(['number', 'type'], '') === 'technical_number';
    let prettyformattedNumber = '';
    if (!isTechnicalNumber) {
        prettyformattedNumber =
            number && isE164Number(callRecord)
                ? prettyFormatE164Number(number, loggedInUserCountryCode)
                : number;
    }

    if (!title) {
        const department = getDepartment(callRecord);
        if (department && title !== department) {
            if (
                title &&
                prettyformattedNumber &&
                title !== prettyformattedNumber
            ) {
                return `${numberWrapper(
                    prettyformattedNumber,
                )}\u00A0\u00A0\u00A0${department}`;
            }
            return department;
        }

        const company = callRecord.getIn(['contact', 'companyName']);
        if (company && title !== company) {
            if (
                title &&
                prettyformattedNumber &&
                title !== prettyformattedNumber
            ) {
                return `${numberWrapper(
                    prettyformattedNumber,
                )}\u00A0\u00A0\u00A0${company}`;
            }
            return company;
        }
    }

    if (prettyformattedNumber && prettyformattedNumber !== title) {
        return numberWrapper(prettyformattedNumber);
    }

    const countryCode = getCountryCodeFromCallRecord(callRecord);
    if (countryCode && title && title !== countryCode) {
        return countries.getName(countryCode, moment.locale());
    }

    return null;
};

export const getCallRecordTitle = (
    callRecord,
    i18next,
    loggedInUserCountryCode,
) => {
    if (!i18next) {
        throw Error('Invalid i18next parameter');
    }

    const title = getCallRecordDisplayText(
        callRecord,
        loggedInUserCountryCode,
        i18next,
    );

    return title || i18next.t('call.hidden_number');
};

export const getCallRecordTitleTW = (
    callRecord,
    i18next,
    numberWrapper,
    loggedInUserCountryCode,
) => {
    if (!i18next) {
        throw Error('Invalid i18next parameter');
    }

    const title = getCallRecordDisplayTextTW(
        callRecord,
        loggedInUserCountryCode,
        i18next,
        numberWrapper,
    );

    return title || i18next.t('call.hidden_number');
};

export const getCallRecordSubtitle = (
    callRecord,
    i18next,
    loggedInUserCountryCode,
    title,
) => {
    if (!i18next) {
        throw Error('Invalid i18next parameter');
    }

    if (!title) {
        throw Error('title must be defined');
    }

    const isTechnicalNumber =
        callRecord.getIn(['number', 'type'], '') === 'technical_number';

    const subtitle = getCallRecordDisplayText(
        callRecord,
        loggedInUserCountryCode,
        i18next,
        title,
    );

    return subtitle || (isTechnicalNumber ? '' : i18next.t('Contact.Unknown'));
};

export const getCallRecordSubtitleTW = (
    callRecord,
    i18next,
    numberWrapper,
    loggedInUserCountryCode,
    title,
) => {
    if (!i18next) {
        throw Error('Invalid i18next parameter');
    }

    if (!title) {
        return null;
    }

    const isTechnicalNumber =
        callRecord.getIn(['number', 'type'], '') === 'technical_number';

    const subtitle = getCallRecordDisplayTextTW(
        callRecord,
        loggedInUserCountryCode,
        i18next,
        numberWrapper,
        title,
    );

    return subtitle || (isTechnicalNumber ? '' : i18next.t('Contact.Unknown'));
};

export const getCallRecordInQueueDuration = (callRecord) => {
    if (!callRecord) {
        return undefined;
    }

    const connectedDate = callRecord.get('connected');
    const answeredDate = callRecord.get('answered');

    if (!connectedDate) {
        return undefined;
    }

    if (answeredDate) {
        return moment
            .duration(moment(answeredDate).diff(moment(connectedDate)))
            .asMilliseconds();
    } else {
        const disconnectedDate = callRecord.get('disconnected');

        if (!disconnectedDate) {
            return undefined;
        }

        return moment
            .duration(moment(disconnectedDate).diff(moment(connectedDate)))
            .asMilliseconds();
    }
};

export const getCallRecordChatSessionId = (callRecord) => {
    if (!callRecord) {
        return undefined;
    }

    return getChatSession(callRecord);
};

export const getCallRecordInCallDuration = (callRecord) => {
    if (!callRecord) {
        return undefined;
    }

    const disconnectedDate = callRecord.get('disconnected');
    const answeredDate = callRecord.get('answered');

    if (!answeredDate || !disconnectedDate) {
        return undefined;
    }

    return moment
        .duration(moment(disconnectedDate).diff(moment(answeredDate)))
        .asMilliseconds();
};

export const getCallRecordAnsweredByAgentNameAbbreviated = (callRecord) => {
    if (!callRecord) {
        return undefined;
    }

    const agentContact = callRecord.getIn(['agent', 'contact']);

    if (!agentContact) {
        return undefined;
    }

    const agentContactFirstName = agentContact.get('firstName');
    const agentContactLastName = agentContact.get('lastName');

    if (!agentContactLastName) {
        return agentContactFirstName;
    }

    return agentContactFirstName + ' ' + agentContactLastName.charAt(0);
};

export const getCallRecordsSortedByConnectedTime = (callRecords) => {
    if (!callRecords) {
        return null;
    }

    return callRecords.sort(callRecordsConnectedTimeSorter);
};

const callRecordsConnectedTimeSorter = (callA, callB) => {
    const dateTimeA = callA ? callA.get('connected') : null;
    const dateTimeB = callB ? callB.get('connected') : null;
    return newestFirstSorter(dateTimeA, dateTimeB);
};

export const getCallRecordsPersonalSortedByTime = (callRecords) => {
    if (!callRecords) {
        return null;
    }

    return callRecords.sort(callRecordsPersonalTimeSorter);
};

const callRecordsPersonalTimeSorter = (callA, callB) => {
    const dateTimeA = callA ? getDateOfPersonalCallRecord(callA) : null;
    const dateTimeB = callB ? getDateOfPersonalCallRecord(callB) : null;
    return newestFirstSorter(dateTimeA, dateTimeB);
};

const voiceMessagesReceivedDateSorter = (voiceMessageA, voiceMessageB) => {
    const dateTimeA = voiceMessageA ? voiceMessageA.get('receivedDate') : null;
    const dateTimeB = voiceMessageB ? voiceMessageB.get('receivedDate') : null;
    return newestFirstSorter(dateTimeA, dateTimeB);
};

export const getVoiceMessagesSortedByReceivedDate = (voiceMessages) => {
    if (!voiceMessages) {
        return null;
    }

    return voiceMessages.sort(voiceMessagesReceivedDateSorter);
};

export const getFirstCallRecordKey = (callRecord) => {
    if (!callRecord) {
        return null;
    }
    return callRecord.first().get('key');
};

export const callRecordIsAnswered = (callRecord) => {
    return !!callRecord.get('answered');
};

export const callRecordIsFinished = (callRecord) => {
    return callRecord.has('disconnected');
};

export const queueCallRecordOnHookWaitingOrdered = (callRecord) => {
    return !!callRecord.get('ohwOrdered');
};

export const callRecordIsMissed = (callRecord) => {
    return (
        callRecordIsIncoming(callRecord) &&
        !callRecord.get('answered') &&
        !!callRecord.get('disconnected') &&
        !callRecord.get('ohwOrdered')
    );
};

export const callRecordIsAbandoned = (callRecord) => {
    return (
        callRecordIsOutgoing(callRecord) &&
        !callRecord.get('answered') &&
        !!callRecord.get('disconnected')
    );
};

export const getCallTypeTextUserFriendly = (callRecord, i18next) => {
    const callIsMissed = callRecordIsMissed(callRecord);
    const callRecordHasVoiceMessage = callRecord
        ? callRecord.get('voiceMessage')
        : false;

    if (callRecordHasVoiceMessage) {
        return i18next.t('call.voicemessage');
    }

    if (callRecordIsIncoming(callRecord)) {
        if (callIsMissed) {
            return i18next.t('call.missed');
        } else {
            return i18next.t('call.incoming');
        }
    } else {
        return i18next.t('call.outgoing');
    }
};

export const getCallRecordHasLookupData = (callRecord) => {
    if (!callRecord) {
        return false;
    }

    return callRecord.getIn(['contact', 'type']) === 'global';
};

export const getCallRecordSourceIcon = (callRecord) => {
    if (!callRecord) {
        return null;
    }

    return callRecord.getIn(['contact', 'sourceIcon']);
};

export const getEarliestConnectedCallRecord = (callRecords) => {
    if (callRecords.isEmpty()) {
        return undefined;
    }

    return callRecords
        .reduce((a, b) => (a.get('connected') < b.get('connected') ? a : b))
        .get('connected');
};

export const getEarliestConnectedCallRecordTimestampInMilliseconds = (
    callRecords,
) => {
    const earliestConnectedTimestamp =
        getEarliestConnectedCallRecord(callRecords);
    return (
        earliestConnectedTimestamp &&
        moment(earliestConnectedTimestamp).subtract(1, 'seconds').format('x')
    );
};

export const callRecordIsIncoming = (callRecord) => {
    return callRecord.get('direction') === 'incoming';
};

export const callRecordIsOutgoing = (callRecord) => {
    return callRecord.get('direction') === 'outgoing';
};

export const getPersonalCallRecordTimestampFormatted = (callRecord) => {
    return moment(getDateOfPersonalCallRecord(callRecord)).format('HH:mm');
};

export const getDateOfPersonalCallRecord = (callRecord) => {
    if (callRecordIsAnswered(callRecord)) {
        return callRecord.get('answered');
    }

    if (callRecordIsMissed(callRecord) || callRecordIsAbandoned(callRecord)) {
        return callRecord.get('disconnected');
    }

    return callRecord.get('connected');
};

export const getCallRecordsSortedInDateGroups = (callRecords) => {
    if (!callRecords) {
        return Map();
    }

    return callRecords
        .groupBy((record) =>
            moment(getDateOfPersonalCallRecord(record)).format('YYYY-MM-DD'),
        )
        .sortBy(
            (_value, key) => key,
            (dateA, dateB) => (dateA < dateB ? 1 : -1),
        )
        .map((dateList) => getCallRecordsPersonalSortedByTime(dateList));
};

export const extractCallRecordIndex = (callRecord) => {
    const contact = callRecord.get('contact');

    return (
        extractCallContact(contact) +
        ' ' +
        callRecord.getIn(['number', 'nationalFormat'], '') +
        ' ' +
        callRecord.getIn(['number', 'number'], '') +
        ' ' +
        callRecord.getIn(['number', 'prettyNationalNumber'], '') +
        ' ' +
        callRecord.getIn(['conferenceDTO', 'name'], '').toLowerCase()
    );
};

export const extractCallContact = (contact = Map()) => {
    return (
        extractContactName(contact) +
        ' ' +
        contact.get('department', '').toLowerCase() +
        ' ' +
        contact.get('companyName', '').toLowerCase()
    );
};

export const callRecordVoiceMessageIsUnread = (callRecord) => {
    if (!callRecord) {
        return false;
    }

    const voiceMessage = callRecord.get('voiceMessage');
    return voiceMessage && voiceMessage.get('voiceMessageState') !== 'read';
};

export const getCallActionMarkedAsHandled = (actions) => {
    if (!actions) return undefined;

    const handleUnansweredActions = actions.filter(
        (action) => action.get('name') === 'handle-unanswered',
    );

    return handleUnansweredActions
        ? handleUnansweredActions.first()
        : undefined;
};

export const getCallRecordActionMarkedAsHandled = (callRecord) => {
    if (!callRecord) return undefined;

    const actions = callRecord.get('actions', List());
    return getCallActionMarkedAsHandled(actions);
};

export const formatNumberToFetchCallRecords = (number, defaultCountryCode) => {
    if (!number.startsWith('+') && !number.startsWith('00')) {
        if (defaultCountryCode === 'se') {
            return number;
        }
        const parsedNumber = parsePhoneNumberFromString(
            number,
            defaultCountryCode.toUpperCase(),
        );

        // Fall back to old method if we can't parse the number
        return (parsedNumber ? parsedNumber.number : number).replace('+', '00');
    }
    if (number.startsWith('+46')) {
        return number.replace('+46', '0');
    }
    if (number.startsWith('0046')) {
        return number.replace('0046', '0');
    }

    return number.replace('+', '00');
};
